import {
  PDFDocument,
  StandardFonts,
  rgb,
  degrees,
  PDFTextField,
  PDFCheckBox,
  PDFDropdown,
  PDFRadioGroup,
} from 'pdf-lib'
import download from 'downloadjs'
import { uid, makeBlankPage } from '../store/projectStore.js'
import { renderPageToDataURL } from './pdfjs.js'
import { renderOrderedPagesToImages } from './pdfToImages.js'
import { stripOutline } from './outline.js'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hexToRgb(hex) {
  const sanitized = (hex || '#000000').replace('#', '')
  const value = parseInt(
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((c) => c + c)
          .join('')
      : sanitized,
    16
  )
  if (Number.isNaN(value)) return rgb(0, 0, 0)
  const r = ((value >> 16) & 255) / 255
  const g = ((value >> 8) & 255) / 255
  const b = (value & 255) / 255
  return rgb(r, g, b)
}

export async function readFileAsBytes(file) {
  const buffer = await file.arrayBuffer()
  return new Uint8Array(buffer)
}

function clamp01(v) {
  if (!Number.isFinite(v)) return 0
  if (v < 0) return 0
  if (v > 1) return 1
  return v
}

// ---------------------------------------------------------------------------
// Document ingestion
// ---------------------------------------------------------------------------

export async function buildDocumentFromFile(file) {
  const bytes = await readFileAsBytes(file)
  const pdf = await PDFDocument.load(bytes, {
    ignoreEncryption: true,
    updateMetadata: false,
  }).catch(() => {
    throw new Error(
      `Could not read "${file.name}". It may be encrypted or corrupted.`
    )
  })

  const pageCount = pdf.getPageCount()
  const docId = uid()
  const pages = []
  for (let i = 0; i < pageCount; i += 1) {
    const sourcePage = pdf.getPage(i)
    const { width, height } = sourcePage.getSize()
    pages.push(
      makeBlankPage({
        id: uid(),
        originalIndex: i,
        width,
        height,
        rotation: 0,
        nativeRotation: sourcePage.getRotation().angle,
      })
    )
  }

  return {
    id: docId,
    file,
    originalName: file.name,
    size: file.size,
    bytes,
    pages,
    formFields: null,
    formScanned: false,
    sourceKind: 'pdf',
  }
}

// ---------------------------------------------------------------------------
// AcroForm scan / write
// ---------------------------------------------------------------------------

const FIELD_HINTS = {
  name: ['name', 'fullname', 'first', 'last'],
  email: ['email', 'mail'],
  phone: ['phone', 'tel', 'mobile'],
  date: ['date', 'dob', 'birthday'],
  address: ['address', 'street', 'city', 'zip', 'postal', 'state', 'country'],
  signature: ['signature', 'sign'],
  amount: ['amount', 'price', 'total', 'sum'],
}

function classifyField(name = '') {
  const lower = name.toLowerCase()
  for (const [kind, hints] of Object.entries(FIELD_HINTS)) {
    if (hints.some((h) => lower.includes(h))) return kind
  }
  return 'text'
}

export async function scanFormFields(doc) {
  const pdf = await PDFDocument.load(doc.bytes, {
    ignoreEncryption: true,
    updateMetadata: false,
  })
  let fields = []
  try {
    const form = pdf.getForm()
    const rawFields = form.getFields()
    fields = rawFields.map((field) => {
      const name = field.getName() || 'Untitled field'
      let type = 'text'
      let value = ''
      let options
      if (field instanceof PDFTextField) {
        type = 'text'
        value = field.getText() || ''
      } else if (field instanceof PDFCheckBox) {
        type = 'checkbox'
        value = field.isChecked()
      } else if (field instanceof PDFDropdown) {
        type = 'dropdown'
        value = field.getSelected()[0] || ''
        options = field.getOptions()
      } else if (field instanceof PDFRadioGroup) {
        type = 'radio'
        value = field.getSelected() || ''
        options = field.getOptions()
      } else {
        type = 'unknown'
      }
      return { name, type, kind: classifyField(name), value, options }
    })
  } catch (_) {
    fields = []
  }
  return fields
}

function applyFormValuesToDocument(pdf, fields) {
  if (!fields || fields.length === 0) return
  let form
  try {
    form = pdf.getForm()
  } catch (_) {
    return
  }
  for (const field of fields) {
    try {
      if (field.type === 'text') {
        const target = form.getTextField(field.name)
        if (field.value !== undefined && field.value !== null) {
          target.setText(String(field.value))
        }
      } else if (field.type === 'checkbox') {
        const target = form.getCheckBox(field.name)
        if (field.value) target.check()
        else target.uncheck()
      } else if (field.type === 'dropdown') {
        const target = form.getDropdown(field.name)
        if (field.value) target.select(String(field.value))
      } else if (field.type === 'radio') {
        const target = form.getRadioGroup(field.name)
        if (field.value) target.select(String(field.value))
      }
    } catch (_) {
      // Skip fields that can't be set
    }
  }
}

function stripMetadata(pdf) {
  try {
    pdf.setTitle('')
    pdf.setAuthor('')
    pdf.setSubject('')
    pdf.setKeywords([])
    pdf.setCreator('')
    pdf.setProducer('')
    const epoch = new Date(0)
    pdf.setCreationDate(epoch)
    pdf.setModificationDate(epoch)
  } catch (_) {
    // best-effort scrub
  }
}

// ---------------------------------------------------------------------------
// Export pipeline (merged)
// ---------------------------------------------------------------------------

export async function buildExportedPdf({
  documents,
  pageOrder,
  exportOptions = {},
  pagesSubset = null, // when set, restricts to these page ids
  signatures = [],
}) {
  const opt = withDefaults(exportOptions)
  const out = await PDFDocument.create({ updateMetadata: !opt.scrubMetadata })
  const font = await out.embedFont(StandardFonts.Helvetica)

  if (opt.scrubMetadata) stripMetadata(out)

  // Pre-embed signatures so we can re-use the embedded resources across pages.
  const sigCache = new Map()
  for (const sig of signatures) {
    if (!sig.dataUrl) continue
    try {
      const [meta, b64] = sig.dataUrl.split(',')
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      const embedded = meta.includes('image/png')
        ? await out.embedPng(bytes)
        : await out.embedJpg(bytes)
      sigCache.set(sig.id, embedded)
    } catch (_) {
      // skip bad signatures
    }
  }

  const sourceCache = new Map()
  const indexMap = new Map()

  for (const doc of documents) {
    const source = await PDFDocument.load(doc.bytes, {
      ignoreEncryption: true,
      updateMetadata: false,
    })
    if (doc.formFields && doc.formFields.length > 0) {
      applyFormValuesToDocument(source, doc.formFields)
      try {
        source.getForm().flatten()
      } catch (_) {
        // not all forms can be flattened; ignore
      }
    }
    sourceCache.set(doc.id, source)
    for (const page of doc.pages) {
      indexMap.set(page.id, { docId: doc.id, page })
    }
  }

  const subsetSet = pagesSubset ? new Set(pagesSubset) : null
  const orderedActive = pageOrder
    .map((id) => indexMap.get(id))
    .filter((entry) => {
      if (!entry) return false
      if (!entry.page.included) return false
      if (subsetSet && !subsetSet.has(entry.page.id)) return false
      return true
    })

  if (orderedActive.length === 0) {
    throw new Error('No pages selected. Enable at least one page before export.')
  }

  const byDoc = new Map()
  for (const entry of orderedActive) {
    if (!byDoc.has(entry.docId)) byDoc.set(entry.docId, new Set())
    byDoc.get(entry.docId).add(entry.page.originalIndex)
  }

  const copyMap = new Map()
  for (const [docId, indexSet] of byDoc.entries()) {
    const source = sourceCache.get(docId)
    const indices = Array.from(indexSet)
    const copies = await out.copyPages(source, indices)
    indices.forEach((origIdx, i) => {
      copyMap.set(`${docId}:${origIdx}`, copies[i])
    })
  }

  let pageNum = (opt.bates && opt.bates.start) || 1
  for (const entry of orderedActive) {
    const key = `${entry.docId}:${entry.page.originalIndex}`
    const copy = copyMap.get(key)
    if (!copy) continue
    const added = out.addPage(copy)
    decoratePage(added, entry.page, { font, sigCache })
    if (opt.watermark.enabled && opt.watermark.text) drawWatermark(added, font, opt.watermark)
    if (opt.bates.enabled) {
      drawBatesNumber(added, font, opt.bates, pageNum)
      pageNum += 1
    }
  }

  if (opt.scrubMetadata) {
    stripMetadata(out)
    stripOutline(out)
  }
  const saveOpts =
    opt.compression === 'compact'
      ? { useObjectStreams: true, objectsPerTick: 80 }
      : { useObjectStreams: false }
  return out.save(saveOpts)
}

function withDefaults(opts) {
  return {
    scrubMetadata: opts.scrubMetadata ?? true,
    bates: {
      enabled: false,
      prefix: '',
      start: 1,
      position: 'bottom-right',
      size: 10,
      color: '#475569',
      ...(opts.bates || {}),
    },
    watermark: {
      enabled: false,
      text: '',
      color: '#94a3b8',
      size: 64,
      opacity: 0.2,
      angle: 45,
      ...(opts.watermark || {}),
    },
    compression: opts.compression || 'normal',
    rasterizeDpi: opts.rasterizeDpi || 110,
    mode: opts.mode || 'merged',
    imageFormat: opts.imageFormat || 'png',
    imageDpi: opts.imageDpi || 144,
  }
}

function drawBatesNumber(page, font, bates, pageNum) {
  const { width, height } = page.getSize()
  const label = `${bates.prefix || ''}${pageNum}`
  const size = bates.size || 10
  const color = hexToRgb(bates.color || '#475569')
  const margin = 24
  const textWidth = font.widthOfTextAtSize(label, size)
  let x = margin
  let y = margin
  switch (bates.position) {
    case 'bottom-left':
      x = margin
      y = margin
      break
    case 'bottom-center':
      x = (width - textWidth) / 2
      y = margin
      break
    case 'bottom-right':
      x = width - textWidth - margin
      y = margin
      break
    case 'top-left':
      x = margin
      y = height - size - margin
      break
    case 'top-center':
      x = (width - textWidth) / 2
      y = height - size - margin
      break
    case 'top-right':
      x = width - textWidth - margin
      y = height - size - margin
      break
    default:
      x = width - textWidth - margin
      y = margin
  }
  page.drawText(label, { x, y, size, font, color })
}

function drawWatermark(page, font, wm) {
  const { width, height } = page.getSize()
  const text = wm.text || ''
  if (!text) return
  const size = wm.size || 64
  const color = hexToRgb(wm.color || '#94a3b8')
  const opacity = clamp01(wm.opacity ?? 0.2)
  const angle = wm.angle ?? 45
  const textWidth = font.widthOfTextAtSize(text, size)
  // Center the text on the page, then rotate around its center.
  const cx = width / 2
  const cy = height / 2
  page.drawText(text, {
    x: cx - textWidth / 2,
    y: cy - size * 0.36,
    size,
    font,
    color,
    opacity,
    rotate: degrees(angle),
  })
}

function decoratePage(page, pageState, { font, sigCache }) {
  if (pageState.rotation) {
    const native = page.getRotation().angle || 0
    const total = (native + pageState.rotation) % 360
    page.setRotation(degrees(total))
  }

  const { width, height } = page.getSize()

  if (pageState.crop) {
    const c = pageState.crop
    const cx = clamp01(c.xRatio) * width
    const cy = clamp01(c.yRatio) * height
    const cw = clamp01(c.wRatio) * width
    const ch = clamp01(c.hRatio) * height
    const pdfX = cx
    const pdfY = height - cy - ch
    const safeW = Math.max(1, cw)
    const safeH = Math.max(1, ch)
    try {
      page.setCropBox(pdfX, pdfY, safeW, safeH)
    } catch (_) {}
  }

  // Annotations are drawn BENEATH stamps so stamps win visually
  for (const ann of pageState.annotations || []) {
    if (ann.type === 'highlight') {
      const w = clamp01(ann.wRatio) * width
      const h = clamp01(ann.hRatio) * height
      if (w <= 0 || h <= 0) continue
      page.drawRectangle({
        x: clamp01(ann.xRatio) * width,
        y: height - clamp01(ann.yRatio) * height - h,
        width: w,
        height: h,
        color: hexToRgb(ann.color || '#fde047'),
        opacity: 0.35,
      })
    } else if (ann.type === 'ink') {
      const color = hexToRgb(ann.color || '#0f172a')
      const thickness = ann.size || 2
      const pts = ann.points || []
      for (let i = 1; i < pts.length; i += 1) {
        const a = pts[i - 1]
        const b = pts[i]
        page.drawLine({
          start: { x: a.x * width, y: height - a.y * height },
          end: { x: b.x * width, y: height - b.y * height },
          thickness,
          color,
          opacity: 1,
        })
      }
    } else if (ann.type === 'sticky') {
      // Small filled rect with text below; PDF doesn't render rich notes
      // without annotation objects, so we burn a yellow square + label.
      const x = clamp01(ann.xRatio) * width
      const y = height - clamp01(ann.yRatio) * height
      const sz = 16
      page.drawRectangle({
        x,
        y: y - sz,
        width: sz,
        height: sz,
        color: hexToRgb(ann.color || '#fde68a'),
        borderColor: rgb(0.7, 0.55, 0.1),
        borderWidth: 0.6,
      })
      if (ann.text) {
        const tSize = 9
        page.drawText(ann.text.slice(0, 80), {
          x: x + sz + 4,
          y: y - sz + 2,
          size: tSize,
          font,
          color: hexToRgb('#1e293b'),
        })
      }
    } else if (ann.type === 'signature') {
      const sig = sigCache.get(ann.signatureId)
      if (!sig) continue
      const w = clamp01(ann.wRatio) * width
      const h = clamp01(ann.hRatio) * height
      if (w <= 0 || h <= 0) continue
      page.drawImage(sig, {
        x: clamp01(ann.xRatio) * width,
        y: height - clamp01(ann.yRatio) * height - h,
        width: w,
        height: h,
      })
    }
  }

  // Stamps — centered on click
  for (const stamp of pageState.stamps || []) {
    const text = stamp.text || ''
    if (!text) continue
    const size = stamp.size || 16
    const color = hexToRgb(stamp.color || '#dc2626')
    const textWidth = font.widthOfTextAtSize(text, size)
    const cx = stamp.xRatio * width
    const cyFromTop = stamp.yRatio * height
    page.drawText(text, {
      x: cx - textWidth / 2,
      y: height - cyFromTop - size * 0.36,
      size,
      font,
      color,
    })
  }

  // Redactions — burned over everything else
  for (const rect of pageState.redactions || []) {
    const w = clamp01(rect.wRatio) * width
    const h = clamp01(rect.hRatio) * height
    if (w <= 0 || h <= 0) continue
    page.drawRectangle({
      x: clamp01(rect.xRatio) * width,
      y: height - clamp01(rect.yRatio) * height - h,
      width: w,
      height: h,
      color: rgb(0, 0, 0),
      opacity: 1,
    })
  }

  // OCR invisible text layer
  if (pageState.ocrApplied && pageState.ocrText && pageState.ocrText.length) {
    for (const word of pageState.ocrText) {
      if (!word.text) continue
      const wWidth = clamp01(word.wRatio) * width
      const wHeight = clamp01(word.hRatio) * height
      if (wWidth <= 0 || wHeight <= 0) continue
      const baseSize = Math.max(4, wHeight)
      let size = baseSize
      const measuredAtBase = font.widthOfTextAtSize(word.text, baseSize)
      if (measuredAtBase > 0 && wWidth > 0) {
        size = Math.max(4, Math.min(64, (wWidth / measuredAtBase) * baseSize))
      }
      try {
        page.drawText(word.text, {
          x: clamp01(word.xRatio) * width,
          y: height - clamp01(word.yRatio) * height - wHeight,
          size,
          font,
          color: rgb(0, 0, 0),
          opacity: 0,
        })
      } catch (_) {
        // glyph not encodable in Helvetica — skip
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Rasterized export (heavy compression / "flatten to images")
// ---------------------------------------------------------------------------

export async function buildRasterizedPdf({
  documents,
  pageOrder,
  exportOptions,
  signatures,
  onProgress,
}) {
  // First produce a normal merged PDF in memory, then re-render every page as
  // a JPG and stuff those JPGs into a fresh PDF. This is the most reliable
  // way to dramatically shrink a PDF that has chunky embedded images.
  const opt = withDefaults(exportOptions)

  // We need the decorated bytes so stamps/redactions/annotations are baked
  // into the bitmap. Use the standard pipeline with mode override.
  const decoratedBytes = await buildExportedPdf({
    documents,
    pageOrder,
    exportOptions: { ...opt, mode: 'merged', compression: 'normal' },
    signatures,
  })

  const tmpDocId = uid()
  const tmpDoc = {
    id: tmpDocId,
    file: null,
    originalName: 'decorated.pdf',
    bytes: decoratedBytes,
    size: decoratedBytes.length,
    pages: [],
  }
  const loaded = await PDFDocument.load(decoratedBytes, { updateMetadata: false })
  for (let i = 0; i < loaded.getPageCount(); i += 1) {
    const p = loaded.getPage(i)
    const { width, height } = p.getSize()
    tmpDoc.pages.push(
      makeBlankPage({ id: uid(), originalIndex: i, width, height })
    )
  }
  const tmpOrder = tmpDoc.pages.map((p) => p.id)

  const out = await PDFDocument.create({ updateMetadata: false })
  if (opt.scrubMetadata) stripMetadata(out)
  const total = tmpDoc.pages.length
  const scale = (opt.rasterizeDpi || 110) / 72

  for (let i = 0; i < total; i += 1) {
    const p = tmpDoc.pages[i]
    onProgress && onProgress({ index: i, total })
    const { dataUrl } = await renderPageToDataURL(
      tmpDoc.id,
      tmpDoc.bytes,
      p.originalIndex + 1,
      { maxWidth: Math.round(p.width * scale), scale }
    )
    // Re-encode as JPG for compression
    const jpgDataUrl = await new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/jpeg', 0.78))
      }
      img.onerror = reject
      img.src = dataUrl
    })
    const b64 = jpgDataUrl.split(',')[1]
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    const jpg = await out.embedJpg(bytes)
    const newPage = out.addPage([p.width, p.height])
    newPage.drawImage(jpg, { x: 0, y: 0, width: p.width, height: p.height })
  }
  onProgress && onProgress({ index: total, total })

  return out.save({ useObjectStreams: true })
}

// ---------------------------------------------------------------------------
// Public top-level: download (handles all modes)
// ---------------------------------------------------------------------------

export async function exportAndDownload({
  documents,
  pageOrder,
  exportOptions,
  signatures = [],
  splitGroups = [],
  filename = 'workbench-export.pdf',
  onProgress,
}) {
  const opt = withDefaults(exportOptions || {})

  if (opt.mode === 'images') {
    return exportAsImages({
      documents,
      pageOrder,
      exportOptions: opt,
      filename,
      onProgress,
    })
  }

  if (opt.mode === 'split') {
    return exportAsSplit({
      documents,
      pageOrder,
      exportOptions: opt,
      signatures,
      splitGroups,
      filename,
      onProgress,
    })
  }

  // 'merged' or anything else → single PDF
  let bytes
  if (opt.compression === 'rasterize') {
    bytes = await buildRasterizedPdf({
      documents,
      pageOrder,
      exportOptions: opt,
      signatures,
      onProgress,
    })
  } else {
    bytes = await buildExportedPdf({
      documents,
      pageOrder,
      exportOptions: opt,
      signatures,
    })
  }
  download(bytes, filename, 'application/pdf')
  return { size: bytes.length }
}

async function exportAsImages({ documents, pageOrder, exportOptions, filename, onProgress }) {
  const results = await renderOrderedPagesToImages(documents, pageOrder, {
    format: exportOptions.imageFormat,
    dpi: exportOptions.imageDpi,
    onProgress,
  })
  if (results.length === 0) throw new Error('No included pages to export.')
  // Always zip when there's more than one image.
  if (results.length === 1) {
    download(results[0].blob, results[0].name, results[0].blob.type)
  } else {
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()
    for (const r of results) zip.file(r.name, r.blob)
    const blob = await zip.generateAsync({ type: 'blob' })
    const name = filename.replace(/\.pdf$/i, '') + '-images.zip'
    download(blob, name, 'application/zip')
  }
  return { count: results.length }
}

async function exportAsSplit({
  documents,
  pageOrder,
  exportOptions,
  signatures,
  splitGroups,
  filename,
  onProgress,
}) {
  if (!splitGroups || splitGroups.length === 0) {
    throw new Error('Add at least one split group before exporting in split mode.')
  }
  const groupsWithPages = splitGroups.filter((g) => g.pageIds && g.pageIds.length > 0)
  if (groupsWithPages.length === 0) {
    throw new Error('No groups contain any pages.')
  }
  const baseName = filename.replace(/\.pdf$/i, '')
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()

  let i = 0
  for (const g of groupsWithPages) {
    onProgress && onProgress({ index: i, total: groupsWithPages.length, name: g.name })
    const bytes = await buildExportedPdf({
      documents,
      pageOrder,
      exportOptions: { ...exportOptions, mode: 'merged' },
      signatures,
      pagesSubset: g.pageIds,
    })
    const safe = (g.name || `group-${i + 1}`).replace(/[^a-z0-9_-]+/gi, '_')
    zip.file(`${safe}.pdf`, bytes)
    i += 1
  }
  onProgress && onProgress({ index: i, total: groupsWithPages.length, name: '' })

  const blob = await zip.generateAsync({ type: 'blob' })
  download(blob, `${baseName}-split.zip`, 'application/zip')
  return { count: groupsWithPages.length }
}
